import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Flame, 
  Trash2, 
  StickyNote,
  Vote,
  Bookmark,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Share2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AgGridReact } from 'ag-grid-react';
import { 
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ColumnState,
  type IRowNode,
  type GridReadyEvent,
  type SortChangedEvent,
  type SelectionChangedEvent,
  type GridApi,
  type ICellRendererParams,
} from 'ag-grid-community';
import { Bill, BillDisplayContext, WorkspacePermissions, BILL_POSITIONS } from '../../types/bill';
import type { SavedViewColumns, SavedViewColumnEntry, BillWorkspaceRecord } from '../../types/savedViews';
import { WORKSPACE_COL_POSITION, WORKSPACE_COL_NOTES, customFieldIdFromColId } from '../../types/savedViews';
import NestedBillTable from '../savedViews/NestedBillTable';
import { workspaceValueCell } from '../savedViews/nestedBillCells';
import { BILL_COLUMN_CATALOG } from '../savedViews/billColumnCatalog';
import { getCommitteeNameShort } from '../../utils/committeeUtils';
import { getPositionColorClasses } from '../../utils/positionColors';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import { getBillLinkClasses } from '../../utils/linkStyles';
import SponsorDisplay from '../SponsorDisplay';
import StatefulIcon from '../StatefulIcon';
import { useEntityNotes, useEntityNoteCounts, noteCountFor, type NoteCountsResult } from '../../hooks/useNotesV2';
import { ViewNotesModal } from '../notes';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import CalendarEventDisplay from './CalendarEventDisplay';
import { AmendmentCell } from './AmendmentCell';
import { useTheme } from '@shared/theme/ThemeContext';
import { billTableLightTheme, billTableDarkTheme, reportGridLightTheme, reportGridDarkTheme } from './ag-grid-theme';
import { formatDate } from '../../utils/dateUtils';

// Register AG Grid Community modules once, on demand
let agGridModulesRegistered = false;

export function ensureAgGridModulesRegistered(): void {
  if (!agGridModulesRegistered) {
    ModuleRegistry.registerModules([AllCommunityModule]);
    agGridModulesRegistered = true;
  }
}
// =====================
// TYPE DEFINITIONS
// =====================

interface ReportNote {
  id: string;
  title: string;
  content: string;
  author: string;
  created_at: string;
  tags?: string[];
}

interface BillTableProps {
  bills: Bill[];
  context: BillDisplayContext;
  workspaceId?: string;
  permissions?: WorkspacePermissions;
  selectedBills?: Set<string>;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  trackedBillIds?: Set<string>;
  // Report mode props
  reportMode?: boolean;
  notesDisplayMode?: 'inline' | 'badge' | 'none';
  // Callbacks
  onBillSelection?: (billId: string, selected: boolean) => void;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  onPositionUpdate?: (billId: string, position: string) => void;
  onToggleHot?: (billId: string, isHot: boolean) => void;
  onRemoveBill?: (billId: string) => void;
  onCreateNote?: (bill: Bill) => void;
  onBookmark?: (billId: string, billNumber: string, billTitle: string) => void;
  onVotePredictions?: (bill: Bill) => void;
  /** Fires when the user scrolls near the bottom of the grid (for infinite scroll). */
  onScrollNearEnd?: () => void;
  /** Override bill link href for bill number click and external-link button (e.g. `/public/bills/{billNumber}`). */
  getBillHref?: (bill: Bill) => string;
  /** Override bill number click handler. When provided, replaces the default navigate() call. */
  onBillNavigate?: (bill: Bill) => void;
  /** When true, sponsor/legislator links use /public/... routes instead of authenticated routes. */
  isPublic?: boolean;
  /**
   * My Saved Views (#1144). When set, billColumns drive AG Grid column state,
   * and workspaceColumns render as per-workspace sub-record rows (2+ effective
   * workspaces) or inline columns (exactly 1 — R22, so the Bill Tracking tab
   * always stays flat). Never derived from a report context (PRD §2.1).
   */
  savedView?: {
    columns: SavedViewColumns;
    /** Effective workspace count AFTER the Bill Tracking tab's own-workspace override (R6a). */
    workspaceCount: number;
    /** field_definition_id → display name, for workspace column headers. */
    customFieldNames: Record<string, string>;
    /** field_definition_id → description, hover on the header label. */
    customFieldDescriptions?: Record<string, string>;
  };
  /** Tracked/All toggle in the nested toolbar (Legislation only; R7a). */
  trackedMode?: boolean;
  onTrackedModeChange?: (tracked: boolean) => void;
  /** In-place editing of workspace cells in nested mode (Legislation). */
  recordEditing?: import('../savedViews/nestedBillCells').RecordEditing;
}

// Mapping between AG Grid column field/colId and the parent's sort field names
const SORT_FIELD_MAP: Record<string, string> = {
  'billNumber': 'number',
  'subjects': 'subjects',
  'status': 'status',
  'introducedDate': 'introducedDate',
  'billPosition': 'billPosition',
  'calendarDate': 'calendarDate',
  'lastAmendment': 'lastActivity',
  'sponsors': 'sponsors',
  'houseCommittees': 'houseCommittees',
  'senateCommittees': 'senateCommittees',
};

// =====================
// CELL RENDERER COMPONENTS
// =====================

// Grid context interface - holds all dynamic/callback props so columnDefs stay stable
interface BillTableGridContext {
  workspaceId?: string;
  context_: BillDisplayContext;
  permissions?: WorkspacePermissions;
  notesDisplayMode?: string;
  /** Batch note counts for every row (#1179); badges fall back to their own query only when absent. */
  noteCounts?: NoteCountsResult;
  reportMode: boolean;
  trackedBillIds: Set<string>;
  onToggleHot?: (billId: string, isHot: boolean) => void;
  onNavigate: (billId: string) => void;
  onNotesClick: (bill: Bill) => void;
  onBookmark?: (billId: string, billNumber: string, billTitle: string) => void;
  onVotePredictions?: (bill: Bill) => void;
  onRemoveBill?: (billId: string) => void;
  onPositionUpdate?: (billId: string, position: string) => void;
  onAmendmentClick: (billId: string) => void;
  /** Override bill number href (e.g. for public reports). When provided, used for the external-link anchor. */
  getBillHref?: (bill: Bill) => string;
  /** Override bill number click navigation (e.g. for public reports). When provided, replaces the default navigate() call. */
  onBillNavigate?: (bill: Bill) => void;
  /** When true, sponsor/legislator links use /public/... routes. */
  isPublic?: boolean;
  /** In-place editing of inline (single-workspace) saved-view cells (#1382). */
  recordEditing?: import('../savedViews/nestedBillCells').RecordEditing;
  onOpenRecordNotes: (bill: Bill, record: BillWorkspaceRecord) => void;
}

/** Bill Number & Title cell renderer - pinned left column (includes action icons) */
const BillCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;

  const {
    workspaceId, context_, permissions, notesDisplayMode, reportMode, noteCounts,
    trackedBillIds, onToggleHot, onNavigate, onBillNavigate, getBillHref, onNotesClick,
    onBookmark, onVotePredictions, onRemoveBill,
  } = props.context as BillTableGridContext;

  // Build action icons (same logic as the former ActionsCellRenderer)
  const actions: Array<{
    icon: any;
    onClick: () => void;
    title: string;
    className: string;
    active?: boolean;
  }> = [];

  const handleShareBill = () => {
    const publicUrl = `${window.location.origin}/public/bills/${encodeURIComponent(bill.billNumber)}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      toast.success('Public link copied to clipboard');
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = publicUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Public link copied to clipboard');
    });
  };

  if (!reportMode) {
    if (context_ === 'legislation') {
      if (onBookmark) {
        const isTracked = trackedBillIds?.has(bill.id) || false;
        actions.push({
          icon: Bookmark,
          onClick: () => onBookmark(bill.id, bill.billNumber, bill.title),
          title: 'Add to workspace',
          className: `text-text-tertiary dark:text-text-dark-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary ${isTracked ? 'text-primary-600' : ''}`,
          active: isTracked,
        });
      }
      actions.push({
        icon: Share2,
        onClick: handleShareBill,
        title: 'Copy public link to bill',
        className: 'text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400',
      });
      actions.push({
        icon: Vote,
        onClick: () => onVotePredictions?.(bill),
        title: 'Vote predictions',
        className: 'text-text-tertiary dark:text-text-dark-tertiary hover:text-purple-600 dark:hover:text-purple-400',
      });
    } else {
      actions.push({
        icon: Vote,
        onClick: () => onVotePredictions?.(bill),
        title: 'View vote predictions for this bill',
        className: 'text-text-tertiary dark:text-text-dark-tertiary hover:text-purple-600 dark:hover:text-purple-400',
      });
      actions.push({
        icon: Share2,
        onClick: handleShareBill,
        title: 'Copy public link to bill',
        className: 'text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400',
      });
      if (permissions?.canEdit && onRemoveBill) {
        actions.push({
          icon: Trash2,
          onClick: () => onRemoveBill(bill.id),
          title: 'Remove from workspace tracking',
          className: 'text-text-tertiary dark:text-text-dark-tertiary hover:text-error-600 dark:hover:text-error-500',
        });
      }
    }
  }

  return (
    <div>
      <p className="text-sm font-medium mb-1 flex items-center gap-2">
        {bill.isHot && reportMode && (
          <span title="Hot Bill" className="flex-shrink-0">
            <Flame className="w-4 h-4 text-error-500" fill="currentColor" />
          </span>
        )}
        <span
          className={`${getBillLinkClasses()} truncate`}
          onClick={(e) => { e.stopPropagation(); onBillNavigate ? onBillNavigate(bill) : onNavigate(bill.id); }}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onBillNavigate ? onBillNavigate(bill) : onNavigate(bill.id); }
          }}
        >
          {bill.billNumber || 'No Number'}
        </span>
        <a
          href={getBillHref ? getBillHref(bill) : (workspaceId
            ? `/legislation/${bill.id}?workspace=${workspaceId}&returnTo=workspace&popup=true`
            : `/legislation/${bill.id}?popup=true`)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 transition-colors flex-shrink-0"
          title="Open in new tab"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
        {context_ === 'workspace' && permissions?.canEdit && onToggleHot && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleHot(bill.id, !bill.isHot); }}
            className="flex-shrink-0 hover:scale-110 transition-transform"
            title={bill.isHot ? 'Remove from hot bills' : 'Mark as hot bill'}
          >
            <Flame className="w-4 h-4 text-error-600 dark:text-error-400" fill={bill.isHot ? 'currentColor' : 'none'} />
          </button>
        )}
      </p>
      <p className="text-sm text-text-secondary dark:text-text-dark-secondary cursor-help" title={bill.title || 'No Title'}>
        {bill.title || 'No Title'}
      </p>
      {!reportMode && (
        <div className="flex items-center gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
          {context_ === 'workspace' && notesDisplayMode === 'badge' && workspaceId && (
            <InlineNotesBadge
              billId={bill.id}
              workspaceId={workspaceId}
              batchCount={noteCountFor(noteCounts, bill.id)}
              onClick={() => onNotesClick(bill)}
            />
          )}
          {context_ === 'workspace' && workspaceId && (
            <CustomFieldBadge
              entityType="bill"
              entityId={bill.id}
              entityDisplayName={`${bill.billNumber}: ${bill.title}`}
              workspaceId={workspaceId}
              count={bill.customFieldCount}
              canEdit={permissions?.canEdit || false}
            />
          )}
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); action.onClick(); }}
              className={`p-1 transition-colors ${action.className}`}
              title={action.title}
            >
              {action.active !== undefined ? (
                <StatefulIcon icon={action.icon} active={action.active} size="sm" />
              ) : (
                <action.icon className="w-4 h-4" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Subjects cell renderer */
const SubjectsCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;

  const subjects = bill.categories && bill.categories.length > 0
    ? bill.categories
    : Array.isArray(bill.subject)
      ? bill.subject
      : bill.subject
        ? [bill.subject]
        : [];

  return (
    <p className="text-sm text-text-secondary dark:text-text-dark-secondary break-words">
      {subjects.length > 0 ? subjects.join(', ') : 'No subjects'}
    </p>
  );
};

/** Status cell renderer */
const StatusCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;
  const eventDate = bill.lastActivity
    ? new Date(bill.lastActivity).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  return (
    <div className="leading-tight">
      <span className="text-sm text-text-primary dark:text-text-dark-primary cursor-default">
        {bill.status || 'No status'}
      </span>
      {eventDate && (
        <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-0.5">
          {eventDate}
        </div>
      )}
    </div>
  );
};

/** Position cell renderer */
const PositionCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;
  const { permissions, reportMode, onPositionUpdate } = props.context as BillTableGridContext;

  /*
    canManage, not canEdit: a position is the organisation speaking, so only a
    workspace manager may set one (2026-08-18). A contributor still SEES it —
    they cannot work to a stance they cannot read — which is what the
    read-only branch below already did for viewers and report mode.
  */
  if (permissions?.canManage && onPositionUpdate && !reportMode) {
    return (
      <select
        value={bill.billPosition || 'Pending'}
        onChange={(e) => { e.stopPropagation(); onPositionUpdate(bill.id, e.target.value); }}
        onClick={(e) => e.stopPropagation()}
        className={`text-xs border border-border-light dark:border-border-dark rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full font-medium ${getPositionColorClasses(bill.billPosition || 'Pending')}`}
      >
        {BILL_POSITIONS.map(position => (
          <option
            key={position.value}
            value={position.value}
            className="bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary"
          >
            {position.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPositionColorClasses(bill.billPosition || 'Pending')}`}>
      {bill.billPosition || 'Pending'}
    </span>
  );
};

/**
 * Inline saved-view workspace cell (exactly one workspace, R22) — the same
 * position/notes/custom-field editors the nested layout uses (#1382), reading
 * the bill's single workspace record. A bill with no record in the view's
 * workspace (All bills mode, untracked) has nothing to edit and stays read-only.
 */
const InlineWorkspaceCellRenderer: React.FC<ICellRendererParams & { col: SavedViewColumnEntry }> = (props) => {
  const bill = props.data as Bill;
  const { recordEditing, onOpenRecordNotes } = props.context as BillTableGridContext;
  const record = bill?.billWorkspaceRecords?.[0];
  if (!record) {
    return props.col.colId === WORKSPACE_COL_POSITION && bill?.billPosition ? (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPositionColorClasses(bill.billPosition)}`}>
        {bill.billPosition}
      </span>
    ) : null;
  }
  return (
    <div className="flex items-center h-full min-w-0">
      {workspaceValueCell(bill, record, props.col, onOpenRecordNotes, recordEditing)}
    </div>
  );
};

/** Workspaces cell renderer — org report contexts */
const WorkspacesCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill?.workspaces || bill.workspaces.length === 0) {
    return <span className="text-text-tertiary dark:text-text-dark-tertiary text-sm">—</span>;
  }
  return <WorkspaceBadgeList workspaces={bill.workspaces} />;
};

/** Calendar Date cell renderer */
const CalendarDateCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;
  const ctx = props.context as BillTableGridContext | undefined;
  return (
    <CalendarEventDisplay
      event={bill.nextCalendarEvent}
      billNumber={bill.billNumber}
      billTitle={bill.title}
      variant="compact"
      showIcon={false}
      showLabel={false}
      emptyText="-"
      context={ctx?.isPublic ? 'public' : 'authenticated'}
    />
  );
};

/** Amended cell renderer for Bills/workspace pages — date + count, clickable link to amendments tab */
const AmendedCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;
  const { onAmendmentClick, isPublic } = props.context as BillTableGridContext;


  const displayText = bill.lastAmendment
    ? formatDate(bill.lastAmendment.date)
    : '';

  return (
    <AmendmentCell
      displayText={displayText}
      hasAmendments={!!bill.lastAmendment}
      onClick={isPublic ? undefined : () => onAmendmentClick(bill.id)}
    />
  );
};

/** Amended Date cell renderer for reports — plain text date + count, not a link */
const AmendedDateCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;


  if (!bill.lastAmendment) {
    return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No amendments</span>;
  }

  return (
    <span className="text-sm text-text-primary dark:text-text-dark-primary">
      {formatDate(bill.lastAmendment.date)}
    </span>
  );
};

/** Amendments List cell renderer - individual amendment links */
const AmendmentsListCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;

  if (!bill.amendments || bill.amendments.length === 0) {
    return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  }

  return <AmendmentCell displayText="" hasAmendments={true} amendments={bill.amendments} />;
};

/** Sponsors cell renderer */
const SponsorsCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;
  const ctx = props.context as BillTableGridContext | undefined;
  return (
    <div className="text-xs text-text-secondary dark:text-text-dark-secondary overflow-hidden w-full">
      <SponsorDisplay sponsors={bill.sponsors} billNumber={bill.billNumber} isPublic={ctx?.isPublic} />
    </div>
  );
};

/** Committee cell renderer */
const CommitteeCellRenderer: React.FC<ICellRendererParams & { chamber: 'house' | 'senate' }> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;
  const { chamber } = props;

  if (!bill.committees || bill.committees.length === 0) {
    return <p className="text-xs text-text-secondary dark:text-text-dark-secondary">None assigned</p>;
  }

  const filtered = bill.committees.filter(c => c.chamber?.toLowerCase() === chamber);
  if (filtered.length === 0) {
    return <p className="text-xs text-text-secondary dark:text-text-dark-secondary">None assigned</p>;
  }

  return (
    <p className="text-xs text-text-secondary dark:text-text-dark-secondary overflow-hidden text-ellipsis w-full">
      {filtered.map(c => getCommitteeNameShort(c.name)).join(', ')}
    </p>
  );
};

/** Notes cell renderer for report context */
const NotesCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const bill = props.data as Bill;
  if (!bill) return null;
  const { onNotesClick } = props.context as BillTableGridContext;
  return (
    <InlineNotesCell
      notes={(bill as any).notes || []}
      onNotesClick={() => onNotesClick(bill)}
    />
  );
};

// =====================
// MAIN BILLTABLE COMPONENT
// =====================

const BillTable: React.FC<BillTableProps> = ({
  bills,
  context,
  workspaceId,
  permissions,
  selectedBills = new Set(),
  sortBy,
  sortDirection,
  trackedBillIds = new Set(),
  reportMode = false,
  notesDisplayMode = 'badge',
  onBillSelection,
  onSort,
  onPositionUpdate,
  onToggleHot,
  onRemoveBill,
  onCreateNote: _onCreateNote,
  onBookmark,
  onVotePredictions,
  onScrollNearEnd,
  getBillHref,
  onBillNavigate,
  isPublic,
  savedView,
  trackedMode,
  onTrackedModeChange,
  recordEditing,
}) => {
  ensureAgGridModulesRegistered();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const gridRef = useRef<AgGridReact>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [notesModalBill, setNotesModalBill] = useState<Bill | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  /*
    Saved-view sub-records (#1144). Active only when the view selects workspace
    columns AND spans 2+ workspaces — one workspace renders inline/flat (R22),
    which also keeps the Bill Tracking tab flat by construction (R6a). Never
    keyed on a report context (PRD §2.1).
  */
  const workspaceColumns = useMemo(
    () => savedView?.columns.workspaceColumns ?? [],
    [savedView?.columns.workspaceColumns]
  );
  const visibleWorkspaceColumnCount = workspaceColumns.filter((c) => !c.hide).length;
  const subRecordsActive =
    visibleWorkspaceColumnCount > 0 && (savedView?.workspaceCount ?? 0) > 1;
  const inlineWorkspaceColumns =
    visibleWorkspaceColumnCount > 0 &&
    savedView?.workspaceCount === 1 &&
    savedView.columns.inlineSingleWorkspace !== false;
  // Bills collapsed (sub-records hidden). Seeded from the view's default (R20).
  const [collapsedBillIds, setCollapsedBillIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!subRecordsActive) return;
    setCollapsedBillIds(
      savedView?.columns.subRecordsExpanded === false
        ? new Set(bills.map((b) => b.id))
        : new Set()
    );
    // Re-seed only when the expansion default flips, not on every data page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subRecordsActive, savedView?.columns.subRecordsExpanded]);
  // Sub-record notes open the notes modal against the RECORD's workspace, not the table's.
  const [notesWorkspaceOverride, setNotesWorkspaceOverride] = useState<string | undefined>(undefined);
  const suppressSelectionRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollAnimRef = useRef<number | null>(null);
  const isScrollingRef = useRef(false);


  const isReportContext = context === 'hot-bills-report' || context === 'bill-tracking-report' || context === 'org-hot-bills-report' || context === 'org-bill-tracking-report';
  const isOrgContext = context === 'org-hot-bills-report' || context === 'org-bill-tracking-report';

  const hasAnyNotes = useMemo(() => {
    return bills.some(bill => Array.isArray((bill as any).notes) && (bill as any).notes.length > 0);
  }, [bills]);

  // Save the clicked bill ID so we can scroll back to it on return navigation
  const saveReturnBillId = useCallback((billId: string) => {
    sessionStorage.setItem('billTable_returnBillId', billId);
  }, []);

  // Read and consume return bill ID from sessionStorage on mount
  const returnBillIdRef = useRef<string | null>(
    sessionStorage.getItem('billTable_returnBillId')
  );
  // Clear it immediately so other BillTable instances don't pick it up
  if (returnBillIdRef.current) {
    sessionStorage.removeItem('billTable_returnBillId');
  }

  // Scroll to the return bill row (works with virtualized layout)
  const scrollToBillIfNeeded = useCallback((api: GridApi) => {
    const billId = returnBillIdRef.current;
    if (!billId) return;
    returnBillIdRef.current = null;

    const node = api.getRowNode(billId);
    if (node && node.rowIndex != null) {
      api.ensureIndexVisible(node.rowIndex, 'middle');
      // Brief highlight after row is visible (virtualized rows render asynchronously)
      setTimeout(() => {
        const rowElement = document.querySelector(`[row-id="${billId}"]`) as HTMLElement;
        if (rowElement) {
          rowElement.style.transition = 'background-color 0.3s ease';
          /*
            P3-06: was a literal rgba(124, 58, 237, .1) — #7C3AED, the violet
            family P1-03 retired. It is a var rather than a token class because
            AG Grid rows are styled imperatively here, and a var keeps the light
            and dark values in index.css beside every other surface colour
            instead of hardcoded in a callback.
          */
          rowElement.style.backgroundColor = 'var(--row-return-highlight)';
          setTimeout(() => {
            rowElement.style.backgroundColor = '';
          }, 1500);
        }
      }, 100);
    }
  }, []);

  // Navigation handlers
  const handleBillNumberClick = useCallback((billId: string) => {
    saveReturnBillId(billId);
    const url = workspaceId
      ? `/legislation/${billId}?workspace=${workspaceId}&returnTo=workspace`
      : `/legislation/${billId}`;
    navigate(url);
  }, [navigate, workspaceId, saveReturnBillId]);

  const handleAmendmentClick = useCallback((billId: string) => {
    saveReturnBillId(billId);
    const url = workspaceId
      ? `/legislation/${billId}?tab=amendments&workspace=${workspaceId}&returnTo=workspace`
      : `/legislation/${billId}?tab=amendments`;
    navigate(url);
  }, [navigate, workspaceId, saveReturnBillId]);

  // AG Grid theme: report surfaces get the grey report identity, data surfaces
  // the purple one — the same split StandardTable makes with variant="report".
  const agTheme = useMemo(() => {
    if (reportMode) {
      return theme === 'dark' ? reportGridDarkTheme : reportGridLightTheme;
    }
    return theme === 'dark' ? billTableDarkTheme : billTableLightTheme;
  }, [theme, reportMode]);

  // Detect horizontal scroll state for chevron visibility
  const updateScrollState = useCallback(() => {
    if (!wrapperRef.current || isScrollingRef.current) return;
    const viewport = wrapperRef.current.querySelector('.ag-center-cols-viewport') as HTMLElement;
    if (!viewport) return;
    const { scrollLeft, scrollWidth, clientWidth } = viewport;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);

    // Position left chevron at right edge of pinned columns
    const pinnedContainer = wrapperRef.current.querySelector('.ag-pinned-left-header') as HTMLElement;
    if (pinnedContainer) {
      const pinnedWidth = pinnedContainer.offsetWidth;
      wrapperRef.current.style.setProperty('--ag-chevron-left-offset', `${pinnedWidth}px`);
    }
  }, []);

  // Attach scroll listener to AG Grid's center viewport
  useEffect(() => {
    if (!wrapperRef.current) return;
    const viewport = wrapperRef.current.querySelector('.ag-center-cols-viewport') as HTMLElement;
    if (!viewport) return;

    // Debounced scroll handler to avoid fighting with animations
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateScrollState, 100);
    };

    viewport.addEventListener('scroll', handleScroll);
    // Initial check
    updateScrollState();

    // Also observe resize changes
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(viewport);

    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
      resizeObserver.disconnect();
    };
  }, [updateScrollState, gridApi, bills]);

  // Smooth scroll using requestAnimationFrame (no fighting with scroll events)
  const startScrolling = useCallback((direction: 'left' | 'right') => {
    isScrollingRef.current = true;
    const pixelsPerFrame = direction === 'left' ? -8 : 8;

    const animate = () => {
      if (!wrapperRef.current) return;
      const viewport = wrapperRef.current.querySelector('.ag-center-cols-viewport') as HTMLElement;
      if (!viewport) return;

      viewport.scrollLeft += pixelsPerFrame;
      scrollAnimRef.current = requestAnimationFrame(animate);
    };

    scrollAnimRef.current = requestAnimationFrame(animate);
  }, []);

  const stopScrolling = useCallback(() => {
    if (scrollAnimRef.current) {
      cancelAnimationFrame(scrollAnimRef.current);
      scrollAnimRef.current = null;
    }
    isScrollingRef.current = false;
    // Update state after scrolling stops
    updateScrollState();
  }, [updateScrollState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollAnimRef.current) {
        cancelAnimationFrame(scrollAnimRef.current);
      }
    };
  }, []);

  // Row ID getter
  const getRowId = useCallback((params: any) => params.data.id, []);

  // Auto-size row height for multi-line bill title + action icons row
  const getRowHeight = useCallback(() => 95, []);

  // Grid context object - holds all dynamic/callback props so columnDefs stay stable.
  // Cell renderers access these via props.context instead of cellRendererParams.
  // Using useMemo here is fine because changes to this object don't cause AG Grid
  // to recreate columns; it just updates what cell renderers see on next render.
  const handleToggleBillCollapse = useCallback((billId: string) => {
    setCollapsedBillIds((prev) => {
      const next = new Set(prev);
      if (next.has(billId)) next.delete(billId);
      else next.add(billId);
      return next;
    });
  }, []);

  const handleOpenSubRecordNotes = useCallback(
    (bill: Bill, record: BillWorkspaceRecord) => {
      // The record's workspace, not the table's — a sub-record may belong to
      // any workspace in the view's set.
      setNotesWorkspaceOverride(record.workspaceId);
      setNotesModalBill(bill);
    },
    []
  );

  // One request for every row's note count instead of one per row (#1179).
  const noteCounts = useEntityNoteCounts(
    'bill',
    bills.map((b) => b.id),
    workspaceId,
    context === 'workspace' && notesDisplayMode === 'badge' && !!workspaceId && !reportMode
  );

  const gridContext = useMemo<BillTableGridContext>(() => ({
    workspaceId,
    context_: context,
    permissions,
    notesDisplayMode,
    reportMode,
    noteCounts,
    trackedBillIds,
    onToggleHot,
    onNavigate: handleBillNumberClick,
    onNotesClick: setNotesModalBill,
    onBookmark,
    onVotePredictions,
    onRemoveBill,
    onPositionUpdate,
    onAmendmentClick: handleAmendmentClick,
    getBillHref,
    onBillNavigate,
    isPublic,
    recordEditing,
    onOpenRecordNotes: handleOpenSubRecordNotes,
  }), [
    workspaceId, context, permissions, notesDisplayMode, reportMode, noteCounts,
    trackedBillIds, onToggleHot, handleBillNumberClick, setNotesModalBill,
    onBookmark, onVotePredictions, onRemoveBill, onPositionUpdate, handleAmendmentClick,
    getBillHref, onBillNavigate, isPublic, recordEditing, handleOpenSubRecordNotes,
  ]);

  // Column definitions - only depends on structural props (context type, report mode,
  // notes display mode). Sort state, callbacks, and other dynamic props are NOT included
  // here; they live in gridContext so columnDefs stays stable across re-renders.
  const columnDefs = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [];

    // Bill Number & Title + Actions - PINNED LEFT
    cols.push({
      colId: 'billNumber',
      headerName: 'BILL',
      pinned: 'left',
      width: 250,
      minWidth: 200,
      sortable: true,
      suppressMovable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      autoHeight: true,
      wrapText: true,
      cellRenderer: BillCellRenderer,
      valueGetter: (params: any) => params.data?.billNumber || '',
    });

    // Workspaces column — org report contexts only, placed immediately after Bill (per org-report convention)
    if (isOrgContext) {
      cols.push({
        colId: 'workspaces',
        headerName: 'WORKSPACES',
        width: 240,
        minWidth: 180,
        sortable: false,
        headerClass: 'ag-bill-table-header-uppercase',
        autoHeight: true,
        wrapText: true,
        cellRenderer: WorkspacesCellRenderer,
      });
    }

    // Subjects
    cols.push({
      colId: 'subjects',
      headerName: 'SUBJECTS',
      width: 200,
      minWidth: 150,
      sortable: false,
      headerClass: 'ag-bill-table-header-uppercase',
      autoHeight: true,
      wrapText: true,
      cellRenderer: SubjectsCellRenderer,
    });

    // Status
    cols.push({
      colId: 'status',
      headerName: 'STATUS',
      width: 200,
      minWidth: 140,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      autoHeight: true,
      wrapText: true,
      cellRenderer: StatusCellRenderer,
      valueGetter: (params: any) => params.data?.status || '',
    });

    // Intro Date
    cols.push({
      colId: 'introducedDate',
      headerName: 'INTRO DATE',
      width: 120,
      minWidth: 100,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      valueGetter: (params: any) => params.data?.introducedDate || '',
      comparator: (valueA: string, valueB: string) => {
        const timeA = valueA ? new Date(valueA).getTime() : NaN;
        const timeB = valueB ? new Date(valueB).getTime() : NaN;
        const validA = !Number.isNaN(timeA);
        const validB = !Number.isNaN(timeB);
        if (!validA && !validB) return 0;
        if (!validA) return 1;
        if (!validB) return -1;
        return timeA - timeB;
      },
      cellRenderer: (params: ICellRendererParams) => {
        const bill = params.data as Bill;
        if (!bill?.introducedDate) return <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">-</span>;
        return (
          <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
            {new Date(bill.introducedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        );
      },
    });

    // Position (workspace and non-org report contexts only)
    if (context === 'workspace' || (isReportContext && !isOrgContext)) {
      cols.push({
        colId: 'billPosition',
        headerName: 'POSITION',
        width: 130,
        minWidth: 100,
        sortable: true,
        headerClass: 'ag-bill-table-header-uppercase',
        cellRenderer: PositionCellRenderer,
        valueGetter: (params: any) => params.data?.billPosition || 'Pending',
      });
    }

    // Calendar Date
    cols.push({
      colId: 'calendarDate',
      headerName: 'CALENDAR DATE',
      width: 160,
      minWidth: 130,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      cellRenderer: CalendarDateCellRenderer,
    });

    if (isReportContext) {
      // Reports: two columns — plain date + individual amendment links
      cols.push({
        colId: 'lastAmendment',
        headerName: 'LAST AMENDMENT DATE',
        width: 160,
        minWidth: 120,
        sortable: true,
        headerClass: 'ag-bill-table-header-uppercase',
        cellRenderer: AmendedDateCellRenderer,
      });
      cols.push({
        colId: 'amendmentsList',
        headerName: 'AMENDMENTS LIST',
        width: 200,
        minWidth: 140,
        sortable: false,
        headerClass: 'ag-bill-table-header-uppercase',
        autoHeight: true,
        wrapText: true,
        cellRenderer: AmendmentsListCellRenderer,
      });
    } else {
      // Bills page / workspace: single column, links to amendments tab
      cols.push({
        colId: 'lastAmendment',
        headerName: 'AMENDED',
        width: 160,
        minWidth: 120,
        sortable: true,
        headerClass: 'ag-bill-table-header-uppercase',
        cellRenderer: AmendedCellRenderer,
      });
    }

    // Sponsors
    cols.push({
      colId: 'sponsors',
      headerName: 'SPONSORS',
      width: 180,
      minWidth: 120,
      sortable: false,
      headerClass: 'ag-bill-table-header-uppercase',
      autoHeight: true,
      wrapText: true,
      cellRenderer: SponsorsCellRenderer,
    });

    // Notes (report context only, when notes exist)
    if (notesDisplayMode === 'inline' && isReportContext && hasAnyNotes) {
      cols.push({
        colId: 'notes',
        headerName: 'NOTES',
        width: 220,
        minWidth: 160,
        sortable: false,
        headerClass: 'ag-bill-table-header-uppercase',
        autoHeight: true,
        wrapText: true,
        cellRenderer: NotesCellRenderer,
      });
    }

    // House Committees
    cols.push({
      colId: 'houseCommittees',
      headerName: 'HOUSE COMMITTEES',
      width: 180,
      minWidth: 130,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      autoHeight: true,
      wrapText: true,
      cellRenderer: CommitteeCellRenderer,
      cellRendererParams: { chamber: 'house' },
      valueGetter: (params: any) => {
        const bill = params.data as Bill;
        if (!bill?.committees) return '';
        return bill.committees
          .filter((c: any) => c.chamber?.toLowerCase() === 'house')
          .map((c: any) => getCommitteeNameShort(c.name))
          .join(', ');
      },
    });

    // Senate Committees
    cols.push({
      colId: 'senateCommittees',
      headerName: 'SENATE COMMITTEES',
      width: 180,
      minWidth: 130,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      autoHeight: true,
      wrapText: true,
      cellRenderer: CommitteeCellRenderer,
      cellRendererParams: { chamber: 'senate' },
      valueGetter: (params: any) => {
        const bill = params.data as Bill;
        if (!bill?.committees) return '';
        return bill.committees
          .filter((c: any) => c.chamber?.toLowerCase() === 'senate')
          .map((c: any) => getCommitteeNameShort(c.name))
          .join(', ');
      },
    });

    return cols;
  }, [context, isReportContext, isOrgContext, notesDisplayMode, hasAnyNotes]);

  /*
    Saved-view column additions (#1144), layered onto the stable columnDefs:
    - sub-record mode: a narrow expand/collapse toggle column at the far left;
    - inline mode (exactly one workspace, R22): workspace-level values as
      ordinary grid columns on the header row, reading the single sub-record.
    Kept out of the main memo so its dependency list stays structural.
  */
  const effectiveColumnDefs = useMemo<ColDef[]>(() => {
    let defs = columnDefs;

    if (inlineWorkspaceColumns && savedView) {
      const inlineDefs: ColDef[] = [];
      for (const col of savedView.columns.workspaceColumns) {
        if (col.hide) continue;
        if (col.colId === WORKSPACE_COL_POSITION) {
          // The workspace context already has a native POSITION column.
          if (defs.some((d) => d.colId === 'billPosition')) continue;
          inlineDefs.push({
            colId: WORKSPACE_COL_POSITION,
            headerName: 'POSITION',
            width: col.width ?? 130,
            sortable: false,
            headerClass: 'ag-bill-table-header-uppercase',
            cellRenderer: InlineWorkspaceCellRenderer,
            cellRendererParams: { col },
            valueGetter: (p: any) =>
              p.data?.billWorkspaceRecords?.[0]?.position ?? p.data?.billPosition ?? '',
          });
          continue;
        }
        if (col.colId === WORKSPACE_COL_NOTES) {
          inlineDefs.push({
            colId: WORKSPACE_COL_NOTES,
            headerName: 'NOTES',
            width: col.width ?? 160,
            sortable: false,
            headerClass: 'ag-bill-table-header-uppercase',
            cellRenderer: InlineWorkspaceCellRenderer,
            cellRendererParams: { col },
            valueGetter: (p: any) => {
              const rec = p.data?.billWorkspaceRecords?.[0];
              return rec?.notesCount ? `${rec.notesCount} ${rec.notesCount === 1 ? 'note' : 'notes'}` : '';
            },
          });
          continue;
        }
        const fieldId = customFieldIdFromColId(col.colId);
        if (fieldId) {
          inlineDefs.push({
            colId: col.colId,
            headerName: (savedView.customFieldNames[fieldId] ?? 'CUSTOM FIELD').toUpperCase(),
            width: col.width ?? 160,
            sortable: false,
            headerClass: 'ag-bill-table-header-uppercase',
            cellRenderer: InlineWorkspaceCellRenderer,
            cellRendererParams: { col },
            valueGetter: (p: any) => {
              const value = p.data?.billWorkspaceRecords?.[0]?.customFieldValues?.[fieldId];
              if (value === null || value === undefined || value === '') return '';
              return Array.isArray(value) ? value.join(', ') : String(value);
            },
          });
        }
      }
      defs = [...defs, ...inlineDefs];
    }

    return defs;
  }, [columnDefs, inlineWorkspaceColumns, savedView]);

  // Apply the view's bill-level column state (visibility, order, width).
  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed?.()) return;
    const billColumns = savedView?.columns.billColumns;
    if (!billColumns?.length) return;

    /*
      Checked columns get their stored state — and every OTHER catalog column
      is explicitly hidden. applyColumnState leaves unmentioned columns alone,
      so without the hide list, unchecking a column in the wizard did nothing:
      the view stored only the checked set and the rest stayed visible.
      Identity/affordance columns (bill number, selection, the sub-record
      toggle) are not in the catalog and are left untouched.
    */
    const chosen = new Set(billColumns.map((c) => c.colId));
    const hidden = BILL_COLUMN_CATALOG
      .filter((c) => !chosen.has(c.colId))
      .map((c) => ({ colId: c.colId, hide: true }));

    gridApi.applyColumnState({
      state: [
        ...billColumns.map((c) => ({
          colId: c.colId,
          hide: c.hide === true,
          ...(c.width !== undefined && { width: c.width }),
        })),
        ...hidden,
      ],
      applyOrder: true,
    });
  }, [gridApi, savedView?.columns.billColumns]);

  // Apply sort state via grid API instead of embedding in columnDefs.
  // This prevents columnDefs from changing on every sort interaction.
  const REVERSE_SORT_FIELD_MAP: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};
    for (const [colId, field] of Object.entries(SORT_FIELD_MAP)) {
      map[field] = colId;
    }
    return map;
  }, []);

  // Ref flag: true while the sync-sort useEffect is applying column state,
  // so we can ignore the resulting onSortChanged event.
  const isSyncingSortRef = useRef(false);

  useEffect(() => {
    if (!gridApi) return;
    const colId = sortBy ? (REVERSE_SORT_FIELD_MAP[sortBy] || sortBy) : undefined;
    if (colId && sortDirection) {
      isSyncingSortRef.current = true;
      gridApi.applyColumnState({
        state: [{ colId, sort: sortDirection }],
        defaultState: { sort: null },
      });
      // AG Grid fires onSortChanged synchronously from applyColumnState,
      // so the flag is already consumed by handleSortChanged above.
      // Reset as a safety net in case it wasn't consumed.
      isSyncingSortRef.current = false;
    }
  }, [gridApi, sortBy, sortDirection, REVERSE_SORT_FIELD_MAP]);

  // Handle AG Grid sort changes -> relay to parent
  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    if (!onSort) return;

    // Skip events triggered by the sync-sort useEffect
    if (isSyncingSortRef.current) {
      isSyncingSortRef.current = false;
      return;
    }

    const allColState = event.api.getColumnState();
    const sortModel = allColState
      .filter((col: ColumnState) => col.sort)
      .map((col: ColumnState) => ({ colId: col.colId, sort: col.sort }));

    // If AG Grid cleared the sort (e.g. during a prop-driven re-render),
    // re-apply the parent's current sort state instead of allowing "unsorted".
    if (sortModel.length === 0) {
      const colId = sortBy ? (REVERSE_SORT_FIELD_MAP[sortBy] || sortBy) : undefined;
      if (colId && sortDirection) {
        isSyncingSortRef.current = true;
        event.api.applyColumnState({
          state: [{ colId, sort: sortDirection }],
          defaultState: { sort: null },
        });
        isSyncingSortRef.current = false;
      }
      return;
    }

    const { colId, sort } = sortModel[0];
    if (!colId || !sort) return;

    const parentField = SORT_FIELD_MAP[colId] || colId;
    if (parentField) {
      onSort(parentField, sort as 'asc' | 'desc');
    }
  }, [onSort, sortBy, sortDirection, REVERSE_SORT_FIELD_MAP]);

  // Handle AG Grid selection changes -> relay to parent
  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    if (!onBillSelection || suppressSelectionRef.current) return;

    const selectedNodes = event.api.getSelectedNodes();
    const selectedIds = new Set(selectedNodes.map((node: IRowNode) => node.data?.id).filter(Boolean));

    // Find what changed and notify parent
    bills.forEach(bill => {
      const isSelectedInGrid = selectedIds.has(bill.id);
      const wasSelectedInParent = selectedBills.has(bill.id);
      if (isSelectedInGrid !== wasSelectedInParent) {
        onBillSelection(bill.id, isSelectedInGrid);
      }
    });
  }, [onBillSelection, bills, selectedBills]);

  // Sync parent's selectedBills with AG Grid's selection state
  useEffect(() => {
    if (!gridApi || !onBillSelection) return;

    suppressSelectionRef.current = true;
    gridApi.forEachNode((node: IRowNode) => {
      if (!node.data) return;
      const shouldBeSelected = selectedBills.has(node.data.id);
      const isSelected = node.isSelected();
      if (shouldBeSelected !== isSelected) {
        node.setSelected(shouldBeSelected);
      }
    });
    suppressSelectionRef.current = false;
  }, [gridApi, selectedBills, onBillSelection]);

  // Force AG Grid to re-render cells when non-value row properties change (e.g. isHot).
  // AG Grid skips cell re-renders when the column's valueGetter result is unchanged,
  // so toggling isHot won't update the flame icon without this.
  const billDataFingerprint = useMemo(
    () => bills.map(b => `${b.id}:${b.isHot}:${b.billPosition}`).join(','),
    [bills]
  );

  useEffect(() => {
    if (gridApi && bills.length > 0) {
      gridApi.refreshCells({ force: true });
    }
  }, [gridApi, billDataFingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rendered cells read gridContext once and never re-read it, so the batch note
  // counts landing after mount would leave every badge stuck on its disabled
  // "..." state. Repaint the bill column when the counts resolve.
  useEffect(() => {
    if (gridApi && bills.length > 0) {
      gridApi.refreshCells({ columns: ['billNumber'], force: true });
    }
  }, [gridApi, noteCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite-scroll: detect when the user scrolls near the bottom of the grid
  const handleBodyScroll = useCallback(() => {
    if (!onScrollNearEnd || !gridRef.current?.api) return;
    const api = gridRef.current.api;
    const lastRow = api.getLastDisplayedRowIndex();
    const totalRows = api.getDisplayedRowCount();
    if (totalRows > 0 && totalRows - lastRow <= 10) {
      onScrollNearEnd();
    }
  }, [onScrollNearEnd]);

  // Grid ready handler - also triggers scroll-to-bill on return navigation
  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    scrollToBillIfNeeded(params.api);
  }, [scrollToBillIfNeeded]);

  // Default column properties.
  // sortingOrder removes the "unsorted" state from AG Grid's click cycle
  // so columns toggle between asc ↔ desc only (the app always has a sort).
  const defaultColDef = useMemo<ColDef>(() => ({
    resizable: true,
    cellClass: 'ag-bill-table-cell',
    sortingOrder: ['asc', 'desc'],
  }), []);

  // Memoize selection-related props so AG Grid doesn't see new object
  // references on every render and reset column state (including sort).
  const rowSelectionConfig = useMemo(() =>
    onBillSelection ? {
      mode: 'multiRow' as const,
      headerCheckbox: true,
      checkboxes: true,
      enableClickSelection: false,
    } : undefined,
  [onBillSelection]);

  const selectionColumnDefConfig = useMemo(() =>
    onBillSelection ? {
      pinned: 'left' as const,
      width: 50,
      maxWidth: 50,
      suppressMovable: true,
      sortable: false,
      headerClass: 'ag-bill-table-header-uppercase',
    } : undefined,
  [onBillSelection]);

  /*
    Nested saved-view mode renders the ledger layout (per-bill label bands,
    workspace records beneath each bill) via GroupedTable primitives — not
    AG Grid, whose single-header model cannot repeat headers per record.
    Every flat configuration falls through to the grid below, unchanged.
  */
  if (subRecordsActive && savedView) {
    return (
      <>
        <NestedBillTable
          bills={bills}
          billColumns={savedView.columns.billColumns}
          billActions={
            savedView.columns.actions?.length
              ? {
                  keys: savedView.columns.actions,
                  onBookmark,
                  onVotePredictions,
                  trackedBillIds,
                }
              : undefined
          }
          workspaceColumns={savedView.columns.workspaceColumns}
          labels={{
            customFieldNames: savedView.customFieldNames,
            customFieldDescriptions: savedView.customFieldDescriptions,
          }}
          collapsedBillIds={collapsedBillIds}
          onToggleBill={handleToggleBillCollapse}
          onExpandAll={() => setCollapsedBillIds(new Set())}
          onCollapseAll={() => setCollapsedBillIds(new Set(bills.map((b) => b.id)))}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={onSort}
          onOpenNotes={handleOpenSubRecordNotes}
          trackedMode={trackedMode}
          onTrackedModeChange={onTrackedModeChange}
          editing={recordEditing}
        />
        {notesModalBill && (
          <ViewNotesModalWithNotes
            bill={notesModalBill}
            workspaceId={notesWorkspaceOverride ?? workspaceId}
            onClose={() => {
              setNotesModalBill(null);
              setNotesWorkspaceOverride(undefined);
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="card p-0 relative" data-testid="shared-bill-table-updated">
        {bills.length > 0 ? (
          <div
            className="ag-bill-table-wrapper"
            ref={wrapperRef}
          >
            <AgGridReact
              ref={gridRef}
              theme={agTheme}
              context={gridContext}
              rowData={bills}
              columnDefs={effectiveColumnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              getRowHeight={getRowHeight}
              suppressCellFocus={true}
              rowSelection={rowSelectionConfig}
              selectionColumnDef={selectionColumnDefConfig}
              onGridReady={onGridReady}
              onSortChanged={handleSortChanged}
              onSelectionChanged={handleSelectionChanged}
              onBodyScroll={handleBodyScroll}
              headerHeight={42}
              animateRows={false}
              enableCellTextSelection={true}
            />

            {/* Left scroll chevron - always rendered, visibility controlled via CSS */}
            <button
              className={`ag-scroll-chevron ag-scroll-chevron-left ${canScrollLeft ? 'ag-scroll-chevron-visible' : 'ag-scroll-chevron-hidden'}`}
              onMouseDown={() => startScrolling('left')}
              onMouseUp={stopScrolling}
              onMouseLeave={stopScrolling}
              onTouchStart={() => startScrolling('left')}
              onTouchEnd={stopScrolling}
              aria-label="Scroll table left"
              tabIndex={canScrollLeft ? 0 : -1}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Right scroll chevron - always rendered, visibility controlled via CSS */}
            <button
              className={`ag-scroll-chevron ag-scroll-chevron-right ${canScrollRight ? 'ag-scroll-chevron-visible' : 'ag-scroll-chevron-hidden'}`}
              onMouseDown={() => startScrolling('right')}
              onMouseUp={stopScrolling}
              onMouseLeave={stopScrolling}
              onTouchStart={() => startScrolling('right')}
              onTouchEnd={stopScrolling}
              aria-label="Scroll table right"
              tabIndex={canScrollRight ? 0 : -1}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        ) : null}
      </div>

      {/* Empty State */}
      {bills.length === 0 && (
        <div className="text-center py-12">
          <div className="text-text-tertiary dark:text-text-dark-tertiary">
            No bills found
          </div>
        </div>
      )}

      {/* View Notes Modal */}
      {notesModalBill && (
        isReportContext && (notesModalBill as any).notes ? (
          <ViewNotesModal
            isOpen={true}
            onClose={() => setNotesModalBill(null)}
            notes={((notesModalBill as any).notes || []).map((note: ReportNote) => ({
              id: note.id,
              title: note.title,
              content: note.content,
              created_at: note.created_at,
              updated_at: note.created_at,
              include_in_reports: true,
              author_name: note.author,
              tags: note.tags || []
            }))}
            entityType="bill"
            entityId={notesModalBill.id}
            entityDisplayName={`${notesModalBill.billNumber} - ${notesModalBill.title}`}
            workspaceId={workspaceId}
          />
        ) : (
          <ViewNotesModalWithNotes
            bill={notesModalBill}
            /* A sub-record's notes belong to the record's workspace, which may
               differ from the table's own workspace (saved views span many). */
            workspaceId={notesWorkspaceOverride ?? workspaceId}
            onClose={() => {
              setNotesModalBill(null);
              setNotesWorkspaceOverride(undefined);
            }}
          />
        )
      )}
    </>
  );
};

// =====================
// HELPER COMPONENTS (unchanged)
// =====================

// Inline notes badge component for displaying next to bill number
interface InlineNotesBadgeProps {
  billId: string;
  workspaceId?: string;
  /** Count from the table-level batch; when set, this badge issues no request of its own. */
  batchCount?: number | 'loading';
  onClick?: () => void;
}

const InlineNotesBadge: React.FC<InlineNotesBadgeProps> = ({ billId, workspaceId, batchCount, onClick }) => {
  const { notes, isLoading } = useEntityNotes('bill', billId, workspaceId, 100, batchCount === undefined);
  const noteCount = typeof batchCount === 'number' ? batchCount : notes.length;
  const pending = batchCount === 'loading' || (batchCount === undefined && isLoading);

  if (pending) {
    return (
      <button
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-xs bg-white dark:bg-background-dark-secondary text-text-tertiary dark:text-text-dark-tertiary rounded px-1.5 py-0.5 cursor-wait"
        disabled
      >
        <StickyNote className="w-3.5 h-3.5" />
        <span className="font-medium">...</span>
      </button>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className={`inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 transition-colors cursor-pointer ${
        noteCount > 0
          ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200 hover:bg-primary-200 dark:hover:bg-primary-900/40'
          : 'bg-white dark:bg-background-dark-secondary text-text-tertiary dark:text-text-dark-tertiary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
      }`}
      title={noteCount > 0 ? `View ${noteCount} note${noteCount === 1 ? '' : 's'}` : 'Add a note'}
    >
      <StickyNote className="w-3.5 h-3.5" />
      <span className="font-medium">{noteCount}</span>
    </button>
  );
};

// Inline notes cell component for report context
interface InlineNotesCellProps {
  notes: ReportNote[];
  onNotesClick?: () => void;
}

const InlineNotesCell: React.FC<InlineNotesCellProps> = ({ notes, onNotesClick }) => {
  const stripHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  if (!notes || notes.length === 0) {
    return <span className="text-text-tertiary dark:text-text-dark-tertiary text-sm">No notes</span>;
  }

  const firstNote = notes[0];

  return (
    <div className="w-full cursor-pointer" onClick={(e) => { e.stopPropagation(); onNotesClick?.(); }}>
      <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
        {firstNote.title && (
          <div className="font-medium text-text-primary dark:text-text-dark-primary text-xs mb-1 line-clamp-1">
            {firstNote.title}
          </div>
        )}
        <div className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-2 break-words">
          {stripHtml(firstNote.content)}
        </div>
        <div className="mt-1 flex items-center gap-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
          <span className="truncate">{firstNote.author}</span>
          <span>•</span>
          <span>{new Date(firstNote.created_at).toLocaleDateString()}</span>
          {firstNote.tags && Array.isArray(firstNote.tags) && firstNote.tags.length > 0 && (
            <>
              <span>•</span>
              <div className="flex gap-1">
                {firstNote.tags.slice(0, 2).map((tag: string) => (
                  <span key={tag} className="px-1 py-0.5 bg-background-tertiary dark:bg-background-dark-tertiary rounded text-xs">
                    {tag}
                  </span>
                ))}
                {firstNote.tags.length > 2 && (
                  <span className="text-xs">+{firstNote.tags.length - 2}</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {notes.length > 1 && (
        <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
          +{notes.length - 1} more note{notes.length - 1 === 1 ? '' : 's'} (click to view all)
        </div>
      )}
    </div>
  );
};

// Wrapper component to fetch notes and display the modal
interface ViewNotesModalWithNotesProps {
  bill: Bill;
  workspaceId?: string;
  onClose: () => void;
}

export const ViewNotesModalWithNotes: React.FC<ViewNotesModalWithNotesProps> = ({ bill, workspaceId, onClose }) => {
  const { notes } = useEntityNotes('bill', bill.id, workspaceId, 100);

  return (
    <ViewNotesModal
      isOpen={true}
      onClose={onClose}
      notes={notes.map((note: any) => ({
        id: note.id,
        title: note.title,
        content: note.content,
        created_at: note.created_at,
        updated_at: note.updated_at,
        include_in_reports: note.include_in_reports,
        author_name: note.author_name || 'Unknown',
        tags: note.tags || []
      }))}
      entityType="bill"
      entityId={bill.id}
      entityDisplayName={`${bill.billNumber || 'Unknown'} - ${bill.title || 'No title'}`}
      workspaceId={workspaceId}
    />
  );
};

export default BillTable;
